import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../utils/api";
import Loader from "../../Component/Loader/Loader";
import PlanLayout from "./components/PlanLayout";

interface LegacyGroup {
  v1PlanId: string;
  v1PlanTitle: string;
  v1PlanPrice: number;
  userCount: number;
}

interface V2Plan {
  _id: string;
  ppm_plan_title: string;
  ppm_plan_visibility: "hidden" | "credit_line" | "own_card" | "both";
}

// Users still on an old cs_plan_pricing (V1) id — new signups there are closed now, but existing
// users need a matching hidden V2 plan and a repointed user_plan_pricing_id so wanew.uomox.com's
// plan pages (which only understand V2) show their real plan instead of "Free/Basic".
const LegacyMigration: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [groups, setGroups] = useState<LegacyGroup[]>([]);
  const [v2Plans, setV2Plans] = useState<V2Plan[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [migratingFor, setMigratingFor] = useState<string | null>(null);
  const [isMigratingAll, setIsMigratingAll] = useState<boolean>(false);
  const [migratedIds, setMigratedIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsRes, plansRes]: any[] = await Promise.all([
        getRequest(`/plans/legacy-plan-users`),
        getRequest(`/plans/list?page=1&status=all&limit=500`)
      ]);

      if (groupsRes.status === "success") setGroups(groupsRes.data || []);
      else toast.error(groupsRes.message || "Failed to load legacy plan usage.");

      if (plansRes.status === "success") setV2Plans(plansRes.data || []);
      else toast.error(plansRes.message || "Failed to load plans.");
    } catch (error) {
      toast.error("Something went wrong loading migration data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // One click: create a hidden V2 twin pre-filled from the V1 plan, then select it automatically.
  const handleCreateHiddenTwin = async (group: LegacyGroup) => {
    setCreatingFor(group.v1PlanId);
    try {
      const response: any = await postRequest(`/plans/add`, {
        ppm_plan_title: group.v1PlanTitle,
        ppm_plan_description: `Migrated from legacy plan (${group.userCount} user${group.userCount === 1 ? "" : "s"})`,
        ppm_plan_type: "recurring",
        ppm_plan_monthly_price: group.v1PlanPrice,
        ppm_plan_yearly_price: group.v1PlanPrice * 12,
        ppm_plan_visibility: "hidden"
      });

      if (response.status === "success") {
        toast.success(`Hidden plan "${group.v1PlanTitle}" created.`);
        const newPlan: V2Plan = response.data;
        setV2Plans(prev => [...prev, newPlan]);
        setMapping(prev => ({ ...prev, [group.v1PlanId]: newPlan._id }));
      } else {
        toast.error(response.message || "Failed to create the hidden plan.");
      }
    } catch (error) {
      toast.error("Something went wrong creating the plan.");
    } finally {
      setCreatingFor(null);
    }
  };

  const runMigration = async (mappings: { v1PlanId: string; v2PlanId: string }[]) => {
    try {
      const response: any = await postRequest(`/plans/migrate-legacy-users`, { mappings });
      if (response.status === "success") {
        let totalUpdated = 0;
        const newlyMigrated = new Set(migratedIds);
        (response.data || []).forEach((r: any) => {
          totalUpdated += r.updated || 0;
          if (r.error) toast.error(`${r.v1PlanId}: ${r.error}`);
          else newlyMigrated.add(r.v1PlanId);
        });
        setMigratedIds(newlyMigrated);
        toast.success(`Migrated ${totalUpdated} user${totalUpdated === 1 ? "" : "s"} to their new plan.`);
      } else {
        toast.error(response.message || "Migration failed.");
      }
    } catch (error) {
      toast.error("Something went wrong running the migration.");
    }
  };

  const handleMigrateOne = async (group: LegacyGroup) => {
    const v2PlanId = mapping[group.v1PlanId];
    if (!v2PlanId) return;
    if (!window.confirm(`Move ${group.userCount} user${group.userCount === 1 ? "" : "s"} from "${group.v1PlanTitle}" to the selected plan? This changes their account directly.`)) return;

    setMigratingFor(group.v1PlanId);
    await runMigration([{ v1PlanId: group.v1PlanId, v2PlanId }]);
    setMigratingFor(null);
  };

  const mappedUnmigrated = groups.filter(g => mapping[g.v1PlanId] && !migratedIds.has(g.v1PlanId));

  const handleMigrateAll = async () => {
    if (mappedUnmigrated.length === 0) return;
    const totalUsers = mappedUnmigrated.reduce((n, g) => n + g.userCount, 0);
    if (!window.confirm(`Move ${totalUsers} user${totalUsers === 1 ? "" : "s"} across ${mappedUnmigrated.length} plan${mappedUnmigrated.length === 1 ? "" : "s"} to their mapped new plans? This changes their accounts directly.`)) return;

    setIsMigratingAll(true);
    await runMigration(mappedUnmigrated.map(g => ({ v1PlanId: g.v1PlanId, v2PlanId: mapping[g.v1PlanId] })));
    setIsMigratingAll(false);
  };

  return (
    <>
      {loading && <Loader />}
      <PlanLayout actions={<>
        <button type="button" className="cx-btn cx-btn-light" onClick={load}><i className="ri-refresh-line"></i>Refresh</button>
        <button type="button" className="cx-btn cx-btn-primary" onClick={handleMigrateAll} disabled={isMigratingAll || mappedUnmigrated.length === 0}>
          <i className="ri-arrow-right-line"></i>{isMigratingAll ? "Migrating…" : `Migrate all mapped (${mappedUnmigrated.length})`}
        </button>
      </>}>
        <div className="cx-card">
          <div className="cx-card-h">
            <h6>Legacy plan users</h6>
            <span className="sub">Every user still on an old cs_plan_pricing (V1) plan — new sign-ups there are closed, but existing users need a hidden V2 twin and a repointed plan reference.</span>
          </div>
          <div className="cx-tbl-wrap">
            <table className="cx-tbl">
              <thead>
                <tr>
                  <th>Legacy plan</th>
                  <th className="r">Price</th>
                  <th className="r">Users</th>
                  <th>Map to (V2 plan)</th>
                  <th className="r">Action</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(group => {
                  const done = migratedIds.has(group.v1PlanId);
                  return (
                    <tr key={group.v1PlanId}>
                      <td>
                        <b style={{ fontWeight: 600 }}>{group.v1PlanTitle}</b>
                        <div className="fs12 muted">{group.v1PlanId}</div>
                      </td>
                      <td className="r mono">₹{group.v1PlanPrice.toLocaleString("en-IN")}</td>
                      <td className="r mono">{group.userCount.toLocaleString("en-IN")}</td>
                      <td>
                        {v2Plans.length === 0 ? (
                          <span className="muted fs12">No V2 plans yet</span>
                        ) : (
                          <select
                            className="cx-select"
                            value={mapping[group.v1PlanId] || ""}
                            disabled={done}
                            onChange={(e) => setMapping(prev => ({ ...prev, [group.v1PlanId]: e.target.value }))}
                          >
                            <option value="">Select a plan…</option>
                            {v2Plans.map(p => (
                              <option key={p._id} value={p._id}>
                                {p.ppm_plan_title}{p.ppm_plan_visibility === "hidden" ? " (hidden)" : ""}
                              </option>
                            ))}
                          </select>
                        )}
                        <button
                          type="button"
                          className="cx-btn cx-btn-light cx-btn-xs mt-1"
                          disabled={creatingFor === group.v1PlanId || done}
                          onClick={() => handleCreateHiddenTwin(group)}
                        >
                          <i className="ri-add-line"></i>{creatingFor === group.v1PlanId ? "Creating…" : "Create hidden twin"}
                        </button>
                      </td>
                      <td className="r">
                        {done ? (
                          <span className="badge rounded-1 bg-neon-light-4 text-uppercase">Migrated</span>
                        ) : (
                          <button
                            type="button"
                            className="cx-btn cx-btn-primary cx-btn-xs"
                            disabled={!mapping[group.v1PlanId] || migratingFor === group.v1PlanId}
                            onClick={() => handleMigrateOne(group)}
                          >
                            {migratingFor === group.v1PlanId ? "Migrating…" : "Migrate"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!loading && groups.length === 0 && (
            <div className="cx-empty"><i className="ri-checkbox-circle-line"></i>No users are on a legacy V1 plan — nothing to migrate.</div>
          )}
        </div>
      </PlanLayout>
    </>
  );
};

export default LegacyMigration;
